import {
  EIDPFieldType,
  EIDPElementType,
  type IIDPField,
  type IIDPExtractedField,
  type IIDPTable,
  type IIDPKeyValue,
  type IIDPEntity,
  type IIDPForm,
  type IIDPTextBlock,
  type IIDPListBlock,
  type IIDPBaseElement,
  type IIDPClientField,
  type IIDPFieldLocation,
  type IIDPClientMissingField,
  type IIDPMissingField,
  type IIDPFactGroup,
  type IIDPFieldGroup,
  type IIDPBoundingBox,
} from '../types';
import { normalizeElement } from './normalize';

/**
 * Transforms IDP fields from backend format to client format.
 * Each element type has different properties based on the migration guide.
 *
 * @param fields - Array of IDP elements from backend (discriminated union)
 * @returns Array of client fields with unique IDs and standardized format
 */
export function transformToClientFields(
  fields: IIDPField[],
): IIDPClientField[] {
  const clientFields: IIDPClientField[] = [];

  fields.forEach((field, fieldIndex) => {
    const id = `field-${fieldIndex}`;

    switch (field.element_type) {
      case EIDPElementType.EXTRACTED_FIELD: {
        const ef = field as IIDPExtractedField;
        ef.values.forEach((value, valueIndex) => {
          clientFields.push(
            createClientField(
              `${id}-val-${valueIndex}`,
              ef.name,
              String(value),
              ef,
              typeof value === 'number'
                ? EIDPFieldType.NUMBER
                : EIDPFieldType.STRING,
            ),
          );
        });
        break;
      }
      case EIDPElementType.TABLE: {
        const table = field as IIDPTable;
        const tableData = table.rows.map(row => {
          const rowObj: Record<string, string> = {};
          table.headers.forEach((header, i) => {
            rowObj[header] = row[i] || '';
          });
          return rowObj;
        });
        clientFields.push(
          createClientField(
            id,
            table.title || 'Table',
            JSON.stringify(tableData),
            table,
            EIDPFieldType.TABLE,
          ),
        );
        break;
      }
      case EIDPElementType.KEY_VALUE: {
        const kv = field as IIDPKeyValue;
        clientFields.push(
          createClientField(id, kv.key, kv.value, kv, EIDPFieldType.KEY_VALUE),
        );
        break;
      }
      case EIDPElementType.ENTITY: {
        const entity = field as IIDPEntity;
        clientFields.push(
          createClientField(
            id,
            entity.entity_type,
            JSON.stringify({
              entity_type: entity.entity_type,
              text: entity.text,
              normalized_value: entity.normalized_value,
              context: entity.context,
            }),
            entity,
            EIDPFieldType.ENTITY,
          ),
        );
        break;
      }
      case EIDPElementType.FORM: {
        const form = field as IIDPForm;
        clientFields.push(
          createClientField(
            id,
            form.title || 'Form',
            JSON.stringify({
              headers: ['Field', 'Value'],
              rows: form.fields.map(f => [f.label, f.value]),
            }),
            form,
            EIDPFieldType.FORM,
          ),
        );
        break;
      }
      case EIDPElementType.TEXT: {
        const text = field as IIDPTextBlock;
        clientFields.push(
          createClientField(
            id,
            text.style,
            JSON.stringify({
              content: text.content,
              style: text.style,
              level: text.level,
            }),
            text,
            EIDPFieldType.TEXT,
          ),
        );
        break;
      }
      case EIDPElementType.LIST: {
        const list = field as IIDPListBlock;
        clientFields.push(
          createClientField(
            id,
            list.list_type,
            JSON.stringify({
              list_type: list.list_type,
              items: list.items,
            }),
            list,
            EIDPFieldType.LIST,
          ),
        );
        break;
      }
      default: {
        const normalized = normalizeElement(
          field as unknown as Record<string, unknown>,
        );
        if (normalized) {
          for (const cf of transformToClientFields([normalized])) {
            clientFields.push({ ...cf, id: `${id}-${cf.id}` });
          }
        }
      }
    }
  });

  return clientFields;
}

/**
 * Validates that a bounding box has valid NORMALIZED coordinates (0..1).
 * All values must be finite numbers with positive dimensions and within bounds.
 */
function isValidBoundingBox(bbox: IIDPBoundingBox): boolean {
  const { x, y, width, height } = bbox;

  if (![x, y, width, height].every(Number.isFinite)) return false;
  if (width <= 0 || height <= 0) return false;
  if (x < 0 || y < 0) return false;
  if (x + width > 1.01 || y + height > 1.01) return false;

  return true;
}

/**
 * Threshold separating pixel-space coordinates from (possibly slightly
 * out-of-range) normalized ones. A normalized box tops out near 1; a real pixel
 * box is tens to thousands of px wide. Requiring a coordinate beyond this keeps
 * a marginally-overflowing normalized box (an extraction artifact) classified as
 * invalid rather than misread as a sub-pixel box.
 */
const PIXEL_SPACE_THRESHOLD = 2;

/**
 * Validates a bounding box expressed in ABSOLUTE PIXELS (astral2/rain). Same
 * sanity checks as the normalized case minus the 0..1 ceiling, plus a magnitude
 * gate: at least one coordinate must be clearly pixel-scale (> threshold).
 */
function isValidPixelBoundingBox(bbox: IIDPBoundingBox): boolean {
  const { x, y, width, height } = bbox;

  if (![x, y, width, height].every(Number.isFinite)) return false;
  if (width <= 0 || height <= 0) return false;
  if (x < 0 || y < 0) return false;
  return (
    x > PIXEL_SPACE_THRESHOLD ||
    y > PIXEL_SPACE_THRESHOLD ||
    width > PIXEL_SPACE_THRESHOLD ||
    height > PIXEL_SPACE_THRESHOLD
  );
}

/**
 * Converts a bounding box and page number to a client field location.
 * Returns null for missing or invalid bounding boxes to prevent rendering errors.
 *
 * Accepts both coordinate systems: normalized 0..1 (legacy astral) and absolute
 * pixels (astral2/rain). Pixel boxes are passed through verbatim and tagged
 * `pixel_space`, so the page renderer can normalize them once the native page
 * size is known (see `normalizePixelSpaceFields`).
 *
 * @param bbox - Bounding box (normalized 0..1 OR absolute pixels)
 * @param pageNumber - Page number (1-indexed)
 * @returns Client field location or null if no bounding box or invalid
 */
function convertToLocation(
  bbox: IIDPBoundingBox | null | undefined,
  pageNumber: number | undefined,
): IIDPFieldLocation | null {
  if (!bbox || pageNumber === undefined) return null;
  const base = {
    left: bbox.x,
    top: bbox.y,
    right: bbox.x + bbox.width,
    bottom: bbox.y + bbox.height,
    page_index: pageNumber - 1, // Convert 1-based to 0-based
  };
  if (isValidBoundingBox(bbox)) return base;
  if (isValidPixelBoundingBox(bbox)) return { ...base, pixel_space: true };
  return null;
}

/**
 * Helper function to create a client field from a backend element
 */
function createClientField(
  id: string,
  name: string,
  value: string,
  backendField: IIDPBaseElement,
  type: EIDPFieldType,
): IIDPClientField {
  return {
    id,
    name,
    value,
    confidence: backendField.confidence,
    location: convertToLocation(
      backendField.bounding_box,
      backendField.page_number,
    ),
    document: backendField.document || '',
    element_type: backendField.element_type,
    type,
  };
}

/**
 * Transforms missing fields to client format.
 * Creates client missing fields with empty values for fields that need to be filled in.
 *
 * @param missingFields - Array of missing fields from backend
 * @returns Array of client missing fields with unique IDs and empty values
 */
export function transformMissingFieldsToClient(
  missingFields: IIDPMissingField[],
): IIDPClientMissingField[] {
  return missingFields.map((field, index) => ({
    id: `field-${index}-${field.name}`,
    name: field.name,
    value: field.value || '', // Use provided value (for present fields) or empty string (for missing fields)
    document: field.document,
    type: field.type || EIDPFieldType.STRING, // Default to string if not specified
    description: field.description,
    status: field.status, // Preserve status for filtering in components
    options: field.options, // Pass through select options
    confidence: field.confidence,
    location: convertToLocation(field.bounding_box, field.page_number),
  }));
}

/**
 * Maps backend fact type to EIDPFieldType.
 * Client types: STRING, NUMBER
 *
 * @param factType - Backend fact type string
 * @returns Corresponding EIDPFieldType
 */
export function mapFactTypeToFieldType(factType: string): EIDPFieldType {
  switch (factType) {
    case 'number':
      return EIDPFieldType.NUMBER;
    case 'table':
      return EIDPFieldType.TABLE;
    case 'select':
      return EIDPFieldType.SELECT;
    // text, date, boolean, json, and unknown types render as text input
    case 'text':
    case 'date':
    case 'boolean':
    case 'json':
    default:
      return EIDPFieldType.STRING;
  }
}

/**
 * Converts a single IIDPFact to an IIDPMissingField with the given document name.
 */
function factToMissingField(
  fact: import('../types').IIDPFact,
  document: string,
): IIDPMissingField {
  return {
    name: fact.field,
    document,
    type: mapFactTypeToFieldType(fact.type),
    value: fact.value,
    status: fact.status,
    options: fact.options,
    confidence: fact.confidence,
    page_number: fact.page_number,
    bounding_box: fact.bounding_box,
  };
}

function filterFacts(
  facts: import('../types').IIDPFact[],
  status?: 'missing' | 'present',
): import('../types').IIDPFact[] {
  return status ? facts.filter(f => f.status === status) : facts;
}

/**
 * Transforms IIDPFactInputData facts to IIDPMissingField array.
 * Can filter by status or return all fields.
 *
 * @param idpData - IDP fact input data from useRunChat
 * @param status - Optional status to filter by ('missing' | 'present'), omit to get all fields
 * @returns Array of IIDPMissingField
 */
export function transformFactsToFields(
  idpData: import('../types').IIDPFactInputData | null | undefined,
  status?: 'missing' | 'present',
): IIDPMissingField[] {
  if (!idpData?.facts) return [];

  const document = idpData.source || '';
  return filterFacts(idpData.facts, status).map(fact =>
    factToMissingField(fact, document),
  );
}

/**
 * Transforms fact groups to field groups for UI rendering.
 * Converts IIDPFactGroup[] to IIDPFieldGroup[] with proper type mapping.
 *
 * @param factGroups - Array of fact groups from IIDPFactInputData
 * @param status - Optional status filter ('missing' | 'present'), omit to get all fields
 * @returns Array of IIDPFieldGroup for UI components
 */
export function transformFactGroupsToFieldGroups(
  factGroups: IIDPFactGroup[] | undefined,
  status?: 'missing' | 'present',
): IIDPFieldGroup[] {
  if (!factGroups?.length) return [];

  return factGroups.map(group => ({
    source: group.source,
    fields: filterFacts(group.facts, status).map(fact =>
      factToMissingField(fact, group.source?.name || ''),
    ),
  }));
}

/**
 * Gets field groups from IDP data, supporting both new factGroups format and legacy facts format.
 * Provides backward compatibility for older data structures.
 *
 * @param idpData - IDP data that may contain factGroups or legacy facts
 * @param status - Optional status filter ('missing' | 'present')
 * @returns Array of IIDPFieldGroup
 */
export function getFieldGroupsFromIdpData(
  idpData: import('../types').IIDPFactInputData | null | undefined,
  status?: 'missing' | 'present',
): IIDPFieldGroup[] {
  if (!idpData) return [];

  // Prefer factGroups if available (new format)
  if (idpData.factGroups?.length) {
    return transformFactGroupsToFieldGroups(idpData.factGroups, status);
  }

  // Fall back to legacy facts array (create single group)
  if (idpData.facts?.length) {
    const fields = transformFactsToFields(idpData, status);
    return [
      {
        source: idpData.source ? { name: idpData.source } : undefined,
        fields,
      },
    ];
  }

  return [];
}

/**
 * Converts exception client fields to annotation-compatible IIDPClientField[].
 * Filters to only fields that have location data (bounding box + page number).
 * This allows the existing IDPAnnotationsCanvas to render bounding boxes for exception fields.
 *
 * @param fields - Array of client exception fields
 * @returns Array of IIDPClientField for the annotation canvas
 */
export function transformExceptionFieldsToAnnotationFields(
  fields: IIDPClientMissingField[],
): IIDPClientField[] {
  return fields.flatMap(field =>
    field.location
      ? [
          {
            id: field.id,
            name: field.name,
            value: field.value,
            confidence: field.confidence ?? 0,
            location: field.location,
            document: field.document,
            element_type: EIDPElementType.EXTRACTED_FIELD as const,
            type: field.type,
          },
        ]
      : [],
  );
}
